var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/eventing/nova-eventing-provider.tsx
import * as React from "react";
import { InputType } from "@nova/types";
import invariant from "invariant";
var NovaEventingContext = React.createContext({});
var NovaEventingProvider = ({ children, eventing, unmountEventing, reactEventMapper }) => {
  const eventingRef = React.useRef(eventing);
  if (eventingRef.current !== eventing) {
    eventingRef.current = eventing;
  }
  const unmountEventingRef = React.useRef(unmountEventing || eventing);
  if (unmountEventingRef.current !== unmountEventing) {
    unmountEventingRef.current = unmountEventing || eventing;
  }
  const mapperRef = React.useRef(reactEventMapper);
  if (mapperRef.current !== reactEventMapper) {
    mapperRef.current = reactEventMapper;
  }
  const reactEventing = React.useMemo(
    () => ({
      bubble: (eventWrapper) => __async(void 0, null, function* () {
        const mappedEvent = mapperRef.current(eventWrapper);
        return getBubble(eventingRef, mappedEvent);
      }),
      generateEvent: getGenerateEvent(eventingRef)
    }),
    []
  );
  const reactUnmountEventing = React.useMemo(
    () => ({
      bubble: (eventWrapper) => __async(void 0, null, function* () {
        const mappedEvent = mapperRef.current(eventWrapper);
        return getBubble(unmountEventingRef, mappedEvent);
      }),
      generateEvent: getGenerateEvent(unmountEventingRef)
    }),
    []
  );
  const contextValue = React.useMemo(
    () => ({
      eventing: reactEventing,
      unmountEventing: reactUnmountEventing,
      internal: { eventingRef, unmountEventingRef, mapperRef }
    }),
    [reactEventing, reactUnmountEventing]
  );
  return /* @__PURE__ */ React.createElement(NovaEventingContext.Provider, { value: contextValue }, children);
};
NovaEventingProvider.displayName = "NovaEventingProvider";
var useNovaEventing = () => {
  const { eventing } = React.useContext(NovaEventingContext);
  invariant(
    eventing,
    "Nova Eventing provider must be initialized prior to consumption of eventing!"
  );
  return eventing;
};
var NovaEventingInterceptor = ({ children, interceptor }) => {
  const interceptorRef = React.useRef(interceptor);
  if (interceptorRef.current !== interceptor) {
    interceptorRef.current = interceptor;
  }
  const { internal: rootInternal } = React.useContext(NovaEventingContext);
  if (!rootInternal) {
    invariant(
      rootInternal,
      "Nova Eventing provider must be initialized prior to creating NovaEventingInterceptor!"
    );
  }
  const reactEventing = React.useMemo(
    () => ({
      bubble: (eventWrapper) => __async(void 0, null, function* () {
        const mappedEvent = rootInternal.mapperRef.current(eventWrapper);
        return getBubble(rootInternal.eventingRef, mappedEvent, interceptorRef);
      }),
      generateEvent: getGenerateEvent(rootInternal.eventingRef, interceptorRef)
    }),
    [rootInternal]
  );
  const reactUnmountEventing = React.useMemo(
    () => ({
      bubble: (eventWrapper) => __async(void 0, null, function* () {
        const mappedEvent = rootInternal.mapperRef.current(eventWrapper);
        return getBubble(
          rootInternal.unmountEventingRef,
          mappedEvent,
          interceptorRef
        );
      }),
      generateEvent: getGenerateEvent(
        rootInternal.unmountEventingRef,
        interceptorRef
      )
    }),
    [rootInternal]
  );
  const { eventing, unmountEventing } = React.useMemo(
    () => createInternalEventingPointingToInterceptor(rootInternal, interceptorRef),
    [rootInternal]
  );
  const eventingRef = React.useRef(eventing);
  const unmountEventingRef = React.useRef(unmountEventing);
  const internal = React.useMemo(
    () => __spreadProps(__spreadValues({}, rootInternal), {
      eventingRef,
      unmountEventingRef
    }),
    [rootInternal]
  );
  const contextValue = React.useMemo(
    () => ({
      eventing: reactEventing,
      unmountEventing: reactUnmountEventing,
      internal
    }),
    [reactEventing, reactUnmountEventing, internal]
  );
  return /* @__PURE__ */ React.createElement(NovaEventingContext.Provider, { value: contextValue }, children);
};
NovaEventingInterceptor.displayName = "NovaEventingInterceptor";
var createInternalEventingPointingToInterceptor = (rootInternal, interceptorRef) => {
  const eventing = {
    bubble: (eventWrapper) => __async(void 0, null, function* () {
      return getBubble(rootInternal.eventingRef, eventWrapper, interceptorRef);
    })
  };
  const unmountEventing = {
    bubble: (eventWrapper) => __async(void 0, null, function* () {
      return getBubble(rootInternal.unmountEventingRef, eventWrapper, interceptorRef);
    })
  };
  return {
    eventing,
    unmountEventing
  };
};
var useNovaUnmountEventing = () => {
  const { unmountEventing } = React.useContext(NovaEventingContext);
  invariant(
    unmountEventing,
    "Nova Eventing provider must be initialized prior to consumption of unmountEventing!"
  );
  return unmountEventing;
};
var getBubble = (eventingRef, eventWrapper, interceptorRef) => __async(void 0, null, function* () {
  if (!interceptorRef) {
    return eventingRef.current.bubble(eventWrapper);
  }
  const forwardEvent = (event) => __async(void 0, null, function* () {
    return eventingRef.current.bubble(event);
  });
  let eventToBubble = eventWrapper;
  eventToBubble = yield interceptorRef.current(eventWrapper, forwardEvent);
  return eventToBubble ? eventingRef.current.bubble(eventToBubble) : Promise.resolve();
});
var getGenerateEvent = (eventingRef, interceptorRef) => {
  return (eventWrapper) => __async(void 0, null, function* () {
    var _a;
    const mappedEvent = {
      event: eventWrapper.event,
      source: {
        inputType: InputType.programmatic,
        timeStamp: (_a = eventWrapper.timeStampOverride) != null ? _a : Date.now()
      }
    };
    if (!interceptorRef) {
      return eventingRef.current.bubble(mappedEvent);
    }
    const forwardEvent = (event) => __async(void 0, null, function* () {
      return eventingRef.current.bubble(event);
    });
    let eventToBubble = mappedEvent;
    eventToBubble = yield interceptorRef.current(mappedEvent, forwardEvent);
    return eventToBubble ? eventingRef.current.bubble(eventToBubble) : Promise.resolve();
  });
};
export {
  NovaEventingInterceptor,
  NovaEventingProvider,
  useNovaEventing,
  useNovaUnmountEventing
};
